import { IRegEdSubmissionInput } from '@Queries/RegEdCompliance';
import * as Api from '@ViewModels';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import { Switch } from 'react-router';
import { toast } from 'sonner';
import {
	IImpersonationContextComponentProps,
	IModalContext,
	ImpersonationContextKey,
	ModalChildComponentContextKey,
} from '../../../../../models';
import { useErrorMessages, useUserSession } from '../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../models/Logging';
import { useImageAttachmentMutation } from '../../../../../queries';
import { ImageFileChooserModal } from '../../../../components/ImageFileChooserModal';
import { MediaChooserModal } from '../../../../components/MediaChooser';
import { DefaultHighResHorizontalPixabayImageSearchOptions } from '../../../../components/MediaChooser/models';
import { IModalProps } from '../../../../components/Modal';
import { PrivateRoute } from '../../../../components/PrivateRoute';
import { usePlatformCustomization } from '../../../../components/socialMedia/PlatformCustomization/usePlatformCustomization';
import { EditSocialMediaSendOptions } from '../../../templates/EditSocialMediaSendOptions';
import { useEditSocialMediaPost } from '../hooks';
import { MobileEditSocialMediaPost } from './presentation';

interface MobileEditSocialMediaPostProps extends IImpersonationContextComponentProps, IModalContext {
	onPostUpdated?(): boolean;
}

const MobileEditSocialMediaPostContainerBase = (props: MobileEditSocialMediaPostProps) => {
	const { impersonationContext, parentModal, onPostUpdated } = props;

	const {
		context,
		errorMessages,
		h,
		impersonationCtx,
		isLoaded,
		locationStateCreateRequest,
		postToEdit,
		routeMatch,
		scheduledPost,
		source,
		socialMediaConnections,
		officerFilesRegEd,
		officerReviewerMissing,
		regEdProbeLoading,
		submitRegEdCompliance,
		suggestionId,
		usesRegEd,
	} = useEditSocialMediaPost({ impersonationContext });
	const platformCustomization = usePlatformCustomization({ context, impersonationContext: impersonationCtx });

	const [isSchedulingInProgress, setIsSchedulingInProgress] = React.useState(false);
	const [bypassCompliance, setBypassCompliance] = React.useState(false);
	const [regEdMetadata, setRegEdMetadata] = React.useState<IRegEdSubmissionInput | null>(
		locationStateCreateRequest?.regEdRevise
			? {
					format: locationStateCreateRequest.regEdRevise.format ?? '',
					answers: locationStateCreateRequest.regEdRevise.answers ?? {},
				}
			: null
	);
	const userSession = useUserSession();
	const localErrorMessages = useErrorMessages();
	const { logApiError } = useEventLogging('EditSocialMediaPost');

	// File upload modal state
	const [fileInputModalOpen, setFileInputModalOpen] = React.useState(false);

	// Free images modal state
	const onFreeImageChooserRequestCloseRef = React.useRef<(img?: Api.IFileAttachment, cancel?: boolean) => void>(null);
	const [freeImageModalProps, setFreeImageModalProps] = React.useState<IModalProps>({
		isOpen: false,
		onRequestClose: (result, cancel) => onFreeImageChooserRequestCloseRef.current?.(result, cancel),
	});

	const modalContent = React.useMemo(() => {
		const content = context.postContent || '';
		return content.replace(/<[^>]*>?/gm, '');
	}, [context.postContent]);

	// Image upload mutation
	const imageAttachmentMutation = useImageAttachmentMutation({
		onError: (error: Api.IOperationResultNoValue): void => {
			localErrorMessages.pushApiError(error);
			toast.error('Failed to upload image');
		},
		onSuccess: result => {
			if (result && result.length > 0) {
				context.addActiveImages(images => [...images, result[0]], undefined);
			}
		},
	});

	const onSelectedConnectionsChanged = React.useCallback(
		(selected: Api.IPostTarget[]) => {
			context.setPostTargets(selected);
			context.setHasChanges(true);
		},
		[context]
	);

	const onRequestClose = React.useCallback(() => {
		parentModal?.onRequestClose();
	}, [parentModal]);

	const onShowCancelUpdateConfirmation = React.useCallback(
		(e: React.MouseEvent<HTMLElement>) => {
			e.preventDefault();
			onRequestClose();
		},
		[onRequestClose]
	);

	const onScheduleForLater = React.useCallback(() => {
		h.push(`${routeMatch.url}/send-options`);
	}, [h, routeMatch.url]);

	const onScheduleWithoutCompliance = React.useCallback(() => {
		setRegEdMetadata(null);
		setBypassCompliance(true);
		onScheduleForLater();
	}, [onScheduleForLater]);

	const onScheduleRegEdCompliance = React.useCallback(
		(_e: React.MouseEvent<HTMLElement>, metadata: IRegEdSubmissionInput) => {
			setRegEdMetadata(metadata);
			setBypassCompliance(false);
			onScheduleForLater();
		},
		[onScheduleForLater]
	);

	const onPostNow = React.useCallback(async () => {
		if (isSchedulingInProgress) {
			return;
		}
		setIsSchedulingInProgress(true);
		try {
			const { schedulePost, updatePost } = context;
			// Both compliance flags are false on internal, so its behaviour is unchanged.
			// eslint-disable-next-line unicorn/prefer-ternary
			if (postToEdit || scheduledPost) {
				await updatePost(scheduledPost, moment(), usesRegEd, undefined, usesRegEd);
			} else {
				await schedulePost(moment(), suggestionId, impersonationCtx, usesRegEd, undefined, usesRegEd);
			}
			if (onPostUpdated?.()) {
				return;
			}
		} catch {
			// Error handling is done in context
		} finally {
			setIsSchedulingInProgress(false);
		}
	}, [
		context,
		impersonationCtx,
		isSchedulingInProgress,
		onPostUpdated,
		postToEdit,
		scheduledPost,
		suggestionId,
		usesRegEd,
	]);

	// File upload handlers
	const handleFileInputModalClose = React.useCallback(
		(val?: File | null, cancel?: boolean) => {
			setFileInputModalOpen(false);
			if (val && !cancel) {
				imageAttachmentMutation.mutate({ files: [val], impersonationContext });
			}
		},
		[imageAttachmentMutation, impersonationContext]
	);

	const onUploadMedia = React.useCallback(() => {
		setFileInputModalOpen(true);
	}, []);

	// Free images handlers
	const onFreeImages = React.useCallback(() => {
		setFreeImageModalProps(value => ({
			...value,
			isOpen: true,
		}));
	}, []);

	const onFreeImageChooserRequestClose = React.useCallback(
		(imageFile?: Api.IFileAttachment, cancel?: boolean) => {
			if (cancel || !imageFile) {
				setFreeImageModalProps(value => ({
					...value,
					isOpen: false,
				}));
				return;
			}

			context.addActiveImages(images => [...images, imageFile], undefined);

			setFreeImageModalProps(value => ({
				...value,
				isOpen: false,
			}));
		},
		[context]
	);

	onFreeImageChooserRequestCloseRef.current = onFreeImageChooserRequestClose;

	const onSubmitPost = React.useCallback(
		async (_: React.MouseEvent<HTMLElement> | undefined, selectedSendTime?: moment.Moment) => {
			if (isSchedulingInProgress) {
				return;
			}
			setIsSchedulingInProgress(true);
			try {
				const { schedulePost, updatePost } = context;
				if (postToEdit || scheduledPost) {
					const [, error] = await updatePost(scheduledPost, selectedSendTime, bypassCompliance, undefined, usesRegEd);
					if (error) {
						errorMessages.pushApiError(error);
						return;
					}
					if (usesRegEd && !officerFilesRegEd && regEdMetadata && !bypassCompliance) {
						// The PUT response may omit approvalRequests; reload to read the server-built AR id.
						const compliancePost = postToEdit || scheduledPost;
						try {
							await compliancePost?.impersonate(impersonationCtx)?.load();
						} catch (err) {
							logApiError('ReloadSocialMediaPost-Error', err);
						}
						await submitRegEdCompliance(compliancePost, regEdMetadata);
					}
				} else {
					const [post, error] = await schedulePost(
						selectedSendTime,
						suggestionId,
						impersonationCtx,
						bypassCompliance,
						undefined,
						usesRegEd
					);
					if (error) {
						errorMessages.pushApiError(error);
						return;
					}
					if (usesRegEd && !officerFilesRegEd && regEdMetadata && !bypassCompliance) {
						await submitRegEdCompliance(post, regEdMetadata);
					}
				}

				// Navigate back or close modal after successful scheduling
				if (onPostUpdated?.()) {
					return;
				}
				h.goBack();
			} catch {
				// Error handling is done in context
			} finally {
				setIsSchedulingInProgress(false);
			}
		},
		[
			bypassCompliance,
			context,
			errorMessages,
			h,
			impersonationCtx,
			isSchedulingInProgress,
			logApiError,
			onPostUpdated,
			postToEdit,
			officerFilesRegEd,
			regEdMetadata,
			scheduledPost,
			submitRegEdCompliance,
			suggestionId,
			usesRegEd,
		]
	);

	return (
		<>
			<Switch>
				<PrivateRoute path={`${routeMatch.url}/send-options`} userSession={userSession}>
					<EditSocialMediaSendOptions
						selectedUser={context?.selectedCreator}
						sendOnBehalf={
							(context?.selectedCreator?.id !== userSession.user.id && !impersonationContext?.isValid) ||
							impersonationContext?.isValid
						}
						initialStartDate={moment(postToEdit?.dueDate)}
						onSubmitPost={onSubmitPost}
						expirationDate={
							postToEdit?.status !== Api.PostStatus.Draft ? context?.template?.schedule?.expirationDate : null
						}
						hasRegEdFiling={!!regEdMetadata}
						minimumStartDate={regEdMetadata?.earliestSendDate}
						regEdReviewNotice={regEdMetadata?.reviewNotice}
						ignoreCompliance={bypassCompliance}
						templateId={routeMatch.params.templateId}
						isSchedulingInProgress={isSchedulingInProgress}
					/>
				</PrivateRoute>
				<PrivateRoute userSession={userSession}>
					<MobileEditSocialMediaPost
						context={context}
						socialMediaConnections={socialMediaConnections}
						onSelectedConnectionsChanged={onSelectedConnectionsChanged}
						onRequestClose={onRequestClose}
						onScheduleForLater={onScheduleForLater}
						onPostNow={onPostNow}
						onFreeImages={onFreeImages}
						onUploadMedia={onUploadMedia}
						onShowCancelUpdateConfirmation={onShowCancelUpdateConfirmation}
						isSchedulingInProgress={isSchedulingInProgress}
						postToEdit={postToEdit}
						scheduledPost={scheduledPost}
						source={source}
						errorMessages={errorMessages}
						isLoaded={isLoaded}
						usesRegEd={usesRegEd}
						officerFilesRegEd={officerFilesRegEd}
						officerReviewerMissing={officerReviewerMissing}
						regEdProbeLoading={regEdProbeLoading}
						onScheduleRegEdCompliance={onScheduleRegEdCompliance}
						onScheduleWithoutCompliance={onScheduleWithoutCompliance}
						regEdMetadata={regEdMetadata}
						regEdRevisesApprovalRequestId={locationStateCreateRequest?.regEdRevise?.revisesApprovalRequestId}
						impersonationCtx={impersonationCtx}
						platformValidationErrors={platformCustomization.errors}
					/>
				</PrivateRoute>
			</Switch>

			{/* File upload modal */}
			<ImageFileChooserModal
				modalProps={{
					isOpen: fileInputModalOpen,
					onRequestClose: handleFileInputModalClose,
				}}
			/>

			{/* Free images modal */}
			{freeImageModalProps.isOpen && (
				<MediaChooserModal
					content={modalContent}
					imageOnly={true}
					impersonationContext={impersonationContext}
					modalProps={freeImageModalProps}
					onSave={onFreeImageChooserRequestClose}
					useBasicSearch={false}
					imageSearchOptions={DefaultHighResHorizontalPixabayImageSearchOptions}
				/>
			)}
		</>
	);
};

const MobileEditSocialMediaPostContainerBaseWithObserver = observer(MobileEditSocialMediaPostContainerBase);
export const MobileEditSocialMediaPostContainer = inject(
	ImpersonationContextKey,
	ModalChildComponentContextKey
)(MobileEditSocialMediaPostContainerBaseWithObserver);
